import { useEffect, useRef, useState, useCallback } from "react";
import { streamStart, streamHeartbeat, streamStop } from "@/lib/api";
import { logWarn } from "@/lib/log";

const BEAT_SECONDS = 5;

async function heartbeatTick(ctx) {
    if (!ctx.streamIdRef.current) return;
    try {
        const hb = await streamHeartbeat(ctx.streamIdRef.current, BEAT_SECONDS, ctx.obsToken);
        ctx.emitCredits(hb.credits);
        if (hb.stop) {
            await ctx.stopMeter();
            ctx.onDepleted();
        }
    } catch (e) {
        logWarn("[SPCLIVE] heartbeat failed", e);
    }
}

async function beginMeter(ctx) {
    const s = await streamStart(ctx.source, ctx.obsToken, ctx.speed);
    if (ctx.isCancelled()) return;
    ctx.streamIdRef.current = s.stream_id;
    ctx.emitCredits(s.credits);
    ctx.timerRef.current = setInterval(() => heartbeatTick(ctx), BEAT_SECONDS * 1000);
}

export function useStreamMeter({ active, source = "studio", obsToken = null, speed = "standard", onDepleted, onCredits }) {
    const streamIdRef = useRef(null);
    const timerRef = useRef(null);
    const onDepletedRef = useRef(onDepleted);
    const onCreditsRef = useRef(onCredits);
    onDepletedRef.current = onDepleted;
    onCreditsRef.current = onCredits;

    const [credits, setCredits] = useState(null);

    const stopMeter = useCallback(async () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        const id = streamIdRef.current;
        streamIdRef.current = null;
        if (id) {
            try {
                const res = await streamStop(id, obsToken);
                setCredits(res.credits);
                onCreditsRef.current?.(res.credits);
            } catch (e) {
                logWarn("[SPCLIVE] stream stop failed", e);
            }
        }
    }, [obsToken]);

    useEffect(() => {
        if (!active) return undefined;
        let cancelled = false;
        const ctx = {
            source,
            obsToken,
            speed,
            streamIdRef,
            timerRef,
            stopMeter,
            isCancelled: () => cancelled,
            emitCredits: (c) => {
                setCredits(c);
                onCreditsRef.current?.(c);
            },
            onDepleted: () => onDepletedRef.current?.(),
        };
        beginMeter(ctx).catch((e) => logWarn("[SPCLIVE] stream start failed", e));
        return () => {
            cancelled = true;
            stopMeter();
        };
    }, [active, source, obsToken, speed, stopMeter]);

    return { credits };
}
